import React from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Flex, List, Modal, Row, Space, Typography } from 'antd';
import { transformToTitleCase } from '@utils/string';
import { DONE_BUTTON_LABEL } from '@constants/common';
import { EDIT_BUTTON_LABEL } from '@constants/pipelines';
import { PermissionAction } from '@redux/types/types';
import ModalTitle from '@modules/Roles/components/ModalTitle/ModalTitle';
import {
  CUSTOM_LABEL,
  PERMISSION_ACTIONS_LIST,
  PERMISSION_VIEWER_MODAL,
  PERMISSION_WILDCARD,
  PRESET_LABEL,
  PRESET_ROLE_NAMES,
} from '@modules/Roles/constants/roles';
import useRoles from '@modules/Roles/hooks/useRoles';
import { RBACRole } from '@modules/Roles/types/roles';
import styles from './PermissionsViewer.module.scss';

interface Props {
  role: RBACRole;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
}

const { Compact } = Space;
const { Text } = Typography;
const MODAL_WIDTH = 720;
const MODAL_TEST_ID = 'permissions-viewer-modal';

const PermissionsViewer: React.FC<Props> = (props) => {
  const { isPresetRole, permissions } = useRoles();
  const { role, isOpen, onClose, onEdit } = props;

  const preset = isPresetRole(role);

  const getName = (value: string) =>
    PRESET_ROLE_NAMES[value as keyof typeof PRESET_ROLE_NAMES] || value;

  const generalDescription = preset
    ? PERMISSION_VIEWER_MODAL.DESCRIPTIONS.PRESET
    : PERMISSION_VIEWER_MODAL.DESCRIPTIONS.CUSTOM;

  const handleEdit = () => {
    onEdit?.();
    onClose();
  };

  // Helper function to get permission level for a specific asset
  const getPermissionLevel = (asset: string) => {
    // Check if role has wildcard permission
    const wildcardPermission = role.permissions.find((p) => p.asset === PERMISSION_WILDCARD);

    if (wildcardPermission) {
      // If wildcard exists, all assets have the same action as the wildcard
      const action = wildcardPermission.action;

      if (action === PermissionAction.READ) {
        return {
          label: PERMISSION_ACTIONS_LIST.readOnly.label,
          detail: PERMISSION_ACTIONS_LIST.readOnly.description,
        };
      } else if (action === PermissionAction.WRITE) {
        return {
          label: PERMISSION_ACTIONS_LIST.readWrite.label,
          detail: PERMISSION_ACTIONS_LIST.readWrite.description,
        };
      }
    }

    // Check for specific asset permission
    const rolePermission = role.permissions.find((p) => p.asset === asset);

    if (!rolePermission) {
      return {
        label: PERMISSION_ACTIONS_LIST.none.label,
        detail: PERMISSION_ACTIONS_LIST.none.description,
      };
    }

    const action = rolePermission.action;

    if (action === PermissionAction.READ) {
      return {
        label: PERMISSION_ACTIONS_LIST.readOnly.label,
        detail: PERMISSION_ACTIONS_LIST.readOnly.description,
      };
    } else if (action === PermissionAction.WRITE) {
      return {
        label: PERMISSION_ACTIONS_LIST.readWrite.label,
        detail: PERMISSION_ACTIONS_LIST.readWrite.description,
      };
    }

    return {
      label: PERMISSION_ACTIONS_LIST.none.label,
      detail: PERMISSION_ACTIONS_LIST.none.description,
    };
  };

  return (
    <Modal
      title={
        <ModalTitle
          title={getName(role.role_name)}
          subtitle={preset ? PRESET_LABEL : CUSTOM_LABEL}
          onCancel={onClose}
        />
      }
      open={isOpen}
      onCancel={onClose}
      className={styles.modal}
      closable={false}
      centered
      width={MODAL_WIDTH}
      footer={
        <Flex gap="small" justify="flex-end">
          {!preset && (
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {EDIT_BUTTON_LABEL}
            </Button>
          )}
          <Button type="primary" onClick={onClose}>
            {DONE_BUTTON_LABEL}
          </Button>
        </Flex>
      }
      data-testid={MODAL_TEST_ID}
    >
      <p className={styles.modal_description}>{generalDescription}</p>
      <Flex gap="small" justify="flex-start" vertical>
        <Row>
          <Col span={6}>
            <Text type="secondary">{PERMISSION_VIEWER_MODAL.ROLE_NAME}</Text>
          </Col>
          <Col span={18}>
            <Compact direction="vertical">
              <Text strong>{getName(role.role_name) || role.role_name}</Text>
              <Text type="secondary" className={styles.modal_itemDescription}>
                {role.description}
              </Text>
            </Compact>
          </Col>
        </Row>
        <Divider orientation="left" className={styles.modal_divider}>
          {PERMISSION_VIEWER_MODAL.PERMISSIONS}
        </Divider>
        <List
          size="small"
          dataSource={permissions.filter((p) => p.asset !== PERMISSION_WILDCARD)}
          renderItem={(permission) => {
            const permissionLevel = getPermissionLevel(permission.asset);
            return (
              <List.Item className={styles.modal_listItem}>
                <Row className={styles.modal_fullWidth}>
                  <Col span={6}>
                    <Text type="secondary">{transformToTitleCase(permission.asset)}</Text>
                  </Col>
                  <Col span={18}>
                    <Compact direction="vertical">
                      <Text strong>{permissionLevel.label}</Text>
                      <Text type="secondary" className={styles.modal_itemDescription}>
                        {permissionLevel.detail}
                      </Text>
                    </Compact>
                  </Col>
                </Row>
              </List.Item>
            );
          }}
        />
      </Flex>
    </Modal>
  );
};

export default PermissionsViewer;
